/**
 * [LOAD-STATES 2026-09-29, user] NEW FILE — per-tab crash screen.
 *
 * Before this there was no error boundary anywhere, so one render error in any tab blanked the whole
 * dashboard (white screen, header and dock gone). AppLayout wraps each tab's page in this: a crash now
 * shows LoadCrash for THAT tab only, the header and dock keep working, and "Reload section" mounts the
 * page again from scratch (a fresh key) without reloading the browser.
 */
import React from 'react';
import { LoadCrash } from './LoadState';
import { invalidateGetCacheByPrefix } from '../services/api';
import { onLeadPage, reportLeadClientError } from '../services/leadClientErrors'; // [LEAD-ERROR-LOG 2026-10-30]

interface State {
  failed: boolean;
  resetKey: number;
}

export default class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { failed: false, resetKey: 0 };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] a section crashed:', error, info.componentStack);
    // [LEAD-ERROR-LOG 2026-10-30, user] the crash screen on Lead Management reaches App Alerts (the lead_errors card), with where it happened
    if (onLeadPage()) reportLeadClientError({ kind: 'crash', message: String((error as Error)?.message || error), stack: `${(error as Error)?.stack || ''}\n${info.componentStack || ''}` });
  }

  // Drop every cached answer first: if the crash came from a bad response, remounting would read the
  // SAME cached copy and crash again (seen in testing). The page then loads fresh from the server.
  private reload = () => {
    invalidateGetCacheByPrefix(['/']);
    this.setState((s) => ({ failed: false, resetKey: s.resetKey + 1 }));
  };

  render() {
    if (this.state.failed) return <LoadCrash onReload={this.reload} />;
    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}
